/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import DefaultDetailsItem from '@/components/items/DefaultDetailsItem'
import ProcessBatch from '@/components/batches/ProcessBatch'
import { Box, Button, Flex, Stack, useToast } from '@chakra-ui/react'
import { columnsTable, schemaValidation } from './handlers'
import RegularTemplate from '@/components/templates/RegularTemplate'
import { licensedAssetsUrls } from '@/configs/urls'
import { useParams } from 'react-router-dom'
import { getLicensedAsset } from '@/services/licensed-assets'
import { useForm, Controller } from 'react-hook-form'
import ModalBodyCreateAIMetadata from './components/ModalBodyCreateAIMetadata'
import { useAuth0 } from '@auth0/auth0-react'
import { joiResolver } from '@hookform/resolvers/joi'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { setPreviewUrl } from '@/utils/items'
import { setErrorCodeAndMessage } from '@/utils/fetch'
import BuildAIMetadata from './components/BuildAIMetadata'
import {
  ACCEPT_TERMS_LABEL,
  BACK_TO_LICENSED_ASSET_LABEL,
  SHOW_ASSET_METADATA
} from '@/configs/strings/licensed-assets'
import PreBlock from '@/components/others/PreBlock'
import { generalBatchItemStates } from '@/configs/server-enums'
import { getEmbeddingModels } from '@/services/embedding-models'
import { formatEmptyValue } from '@/utils/assets'
import { createAIMetadata } from '@/services/ai-metadata'
import { CREATE_AI_METADATA } from '@/configs/strings/ai-metadata-batches.strings'
import { defaultToastConfig } from '@/configs/toast'
import AIMetadata from './components/AIMetadataList'

const LicensedAsset = () => {
  const toast = useToast()
  const [isCreating, setIsCreating] = useState(false)
  const [hasAlert, setHasAlert] = useState(false)
  const [alert, setAlert] = useState()
  const { licensedAssetId } = useParams()
  const [showBuildAIMetadata, setShowBuildAIMetadata] = useState(false)
  const [showMetadata, setShowMetadata] = useState()
  const numberAssets = 6
  const costPerAsset = 0.75
  const totalCost = +numberAssets * +costPerAsset
  const rowTable = {
    totalCost,
    costPerAsset,
    numberAssets
  }
  const {
    itemsResponse: licensedAsset,
    loading: isLoading,
    reloadItems
  } = useFetchItemsWithParamsV2({
    fetchService: getLicensedAsset,
    fetchParams: {
      licensedAssetId,
      samsState: generalBatchItemStates.created
    }
  })
  const {
    itemsResponse: response,
    loading: isLoadingEmbeddings
  } = useFetchItemsWithParamsV2({
    fetchService: getEmbeddingModels
  })
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const showMetadataTab = () => {
    setShowBuildAIMetadata(false)
    setShowMetadata(true)
  }

  const showBuildAIMetadataTab = () => {
    setShowMetadata(false)
    setShowBuildAIMetadata(true)
  }

  const {
    handleSubmit,
    control,
    register,
    formState: { errors }
  } = useForm({
    resolver: joiResolver(schemaValidation)
  })

  const onSubmit = async ({
    preDealAIStrategies,
    separateIngredients
  }) => {
    try {
      setIsCreating(true)
      setHasAlert(false)
      setAlert(null)
      const data = {
        licensedAssetId,
        embeddingModelIds: preDealAIStrategies,
        separateIngredients
      }
      const aiMetadata = await createAIMetadata(data, getAccessToken)

      toast({
        title: licensedAsset?.asset?.title,
        description: `The AI Metadata for ${licensedAsset?.asset?.title} is being processed; we will notify you when it is finished.`,
        status: 'success',
        ...defaultToastConfig
      })

      setIsCreating(false)

      aiMetadata.length > 0 &&
        reloadItems()
    } catch (error) {
      setHasAlert(true)
      setAlert(setErrorCodeAndMessage(error))
      toast({
        title: licensedAsset?.asset?.title,
        description: `Failed processing the AI Metadata for  ${licensedAsset?.asset?.title}. Please contact with us.`,
        status: 'error',
        ...defaultToastConfig
      })
    }

    setIsCreating(false)
  }

  const sendData = async () => await handleSubmit(onSubmit)()

  const imageUrl = setPreviewUrl(licensedAsset?.asset?.coverUrl, licensedAsset?.asset?.fileType)

  const CustomTitle = () => {
    return licensedAsset &&
      <div className="mb-4">
        <h1>{licensedAsset.asset.title}</h1>
        <p>By { formatEmptyValue(
          licensedAsset.asset.creators.map(c => c.name).join(',')
        )}
        </p>
      </div>
  }

  return (
    <RegularTemplate
      loadingContent={isLoading || isCreating}
      backLabel={BACK_TO_LICENSED_ASSET_LABEL}
      backUrl={`/${licensedAssetsUrls.base}`}
      childrenHeader={CustomTitle}
      itemNotFound={!licensedAsset}
    >
      { !isLoading && !isLoadingEmbeddings && licensedAsset &&
        <>
          <Stack direction={['column', 'row']} className="mb-4">
            <Button colorScheme={showBuildAIMetadata ? 'blue' : null} onClick={showBuildAIMetadataTab}>
              { CREATE_AI_METADATA }
            </Button>
            <Button colorScheme={showMetadata ? 'blue' : null} onClick={showMetadataTab}>
              { SHOW_ASSET_METADATA }
            </Button>
          </Stack>

          <section>
            <Flex
              flexDirection={{
                base: 'column',
                md: 'row'
              }}
              gap={4}
            >
              <Box
                flexShrink={0}
              >
                <DefaultDetailsItem
                  imageUrl={imageUrl}
                  details={[
                    { label: 'Title', value: licensedAsset.asset.title },
                    { label: 'Creators', value: formatEmptyValue(licensedAsset.asset.creators.map(c => c.name).join(',')) },
                    { label: 'Publisher', value: formatEmptyValue(licensedAsset.asset.publishers.map(c => c.name).join(',')) },
                    { label: 'File Type', value: licensedAsset.asset.fileType },
                    { label: 'Asset ID', value: licensedAsset.asset.assetId },
                    { label: 'Unique Identifier', value: formatEmptyValue(licensedAsset.asset.uniqueIdentifier) }
                  ]}
                />
              </Box>
              <Box
                className="min-w-0"
                flexGrow={1}
              >
                { showBuildAIMetadata &&
                  <>
                    <BuildAIMetadata
                      controller={Controller}
                      control={control}
                      errors={errors}
                      hasAlert={hasAlert}
                      licensedAsset={licensedAsset}
                      isPdf={licensedAsset.asset.fileType === 'PDF'}
                      register={register}
                    />
                    <ProcessBatch
                      alertMessage={alert?.code}
                      alertStatus="error"
                      bodyModal={
                        () => <ModalBodyCreateAIMetadata
                          costPerAsset={costPerAsset}
                        />
                      }
                      className="mb-4"
                      columnsTable={columnsTable}
                      hasAlert={hasAlert}
                      labelContinue={ACCEPT_TERMS_LABEL}
                      onProcess={sendData}
                      rowTable={rowTable}
                      setHasAlert={setHasAlert}
                      titleModal={CREATE_AI_METADATA}
                    />
                  </>
                }
                { showMetadata &&
                  <PreBlock className="mb-4">
                    { JSON.stringify(licensedAsset.asset.metadata, null, 2) }
                  </PreBlock>
                }
                <AIMetadata
                  assetFileType={licensedAsset.asset.fileType}
                  licensedAssetId={licensedAssetId}
                />
              </Box>
            </Flex>
          </section>
        </>
      }
    </RegularTemplate>
  )
}

export default LicensedAsset
